import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
  Typography, Chip, Alert, Autocomplete, List, ListItem, ListItemText, Divider,
} from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import { rfqAPI, supplierOffersAPI } from '../../services/api';
import downloadFile from '../../services/downloadFile';

/**
 * Peticion de oferta desde la lista de equipos.
 *
 * Se marcan equipos, se eligen varios proveedores y se crea una oferta por cada
 * uno con las mismas partidas: la comparativa es despues "todas las ofertas con
 * este codigo". El documento para enviar se genera aqui mismo.
 */
export default function RequestQuotesDialog({
  open, onClose, projectId, equipments = [], supplierOptions = [], onCreated,
}) {
  const [suppliers, setSuppliers] = useState([]);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setSuppliers([]); setNotes(''); setError(''); setResult(null); setSaving(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const known = [];
      const free = [];
      suppliers.forEach((s) => {
        if (typeof s === 'string') free.push(s);
        else if (s?.id) known.push(s.id);
        else if (s?.label) free.push(s.label);
      });
      const resp = await rfqAPI.create(projectId, {
        equipment_ids: equipments.map((e) => e.id),
        supplier_ids: known,
        supplier_names: free,
        notes: notes || null,
      });
      setResult(resp.data);
      if (onCreated) await onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'No se ha podido crear la peticion.');
    } finally {
      setSaving(false);
    }
  };

  const downloadRequest = async (offerId) => {
    try {
      setError('');
      const resp = await supplierOffersAPI.requestDocument(offerId);
      downloadFile(resp, 'peticion_oferta.docx');
    } catch (err) {
      setError('No se ha podido generar el documento.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {result ? 'Peticiones creadas' : `Pedir oferta de ${equipments.length} equipo(s)`}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {result ? (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              {result.message}. Codigo de licitacion <b>{result.rfq_code}</b>.
            </Alert>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
              Cada peticion ya tiene sus partidas creadas: cuando llegue la oferta solo hay que
              escribir los importes. El documento se descarga en Word, para retocarlo antes de
              enviarlo al proveedor.
            </Typography>
            <List dense>
              {result.offers.map((o) => (
                <ListItem
                  key={o.id}
                  secondaryAction={
                    <Button size="small" startIcon={<DescriptionIcon />} onClick={() => downloadRequest(o.id)}>
                      Word
                    </Button>
                  }
                >
                  <ListItemText primary={o.supplier_name} secondary={`${o.lines} partidas`} />
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary">Equipos incluidos</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, maxHeight: 130, overflowY: 'auto' }}>
                {equipments.map((e) => (
                  <Chip key={e.id} size="small" label={e.tag ? `${e.tag} — ${e.name}` : e.name} />
                ))}
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Autocomplete
              multiple freeSolo size="small" options={supplierOptions}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.label || '')}
              value={suppliers}
              onChange={(_, v) => setSuppliers(v)}
              renderInput={(params) => (
                <TextField {...params} label="Proveedores a los que pedir"
                  placeholder="Escribe para buscar, o un nombre nuevo" />
              )}
            />
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              Se crea una oferta pendiente por proveedor. Un proveedor que no este dado de alta
              se admite igual, escribiendo su nombre.
            </Typography>
            <TextField
              fullWidth multiline rows={2} size="small" sx={{ mt: 2 }}
              label="Notas para el proveedor (opcional)"
              value={notes} onChange={(e) => setNotes(e.target.value)}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{result ? 'Cerrar' : 'Cancelar'}</Button>
        {!result && (
          <Button variant="contained" onClick={handleCreate}
            disabled={saving || suppliers.length === 0 || equipments.length === 0}>
            {saving ? 'Creando...' : `Pedir a ${suppliers.length || 0} proveedor(es)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
